import { Card, CardContent } from "@material-ui/core";
import AppHeader from "@saleor/components/AppHeader";
import { CardSpacer } from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import Metadata, { MetadataFormData } from "@saleor/components/Metadata";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SeoForm from "@saleor/components/SeoForm";
import SingleSelectField from "@saleor/components/SingleSelectField";
import VisibilityCard from "@saleor/components/VisibilityCard";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import useDateLocalize from "@saleor/hooks/useDateLocalize";
import { sectionNames } from "@saleor/intl";
import { CollectionUsedForEnum } from "@saleor/types/globalTypes";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { ContentState, convertToRaw, RawDraftContentState } from "draft-js";
import React from "react";
import { useIntl } from "react-intl";

import CollectionDetails from "../CollectionDetails/CollectionDetails";
import { CollectionImage } from "../CollectionImage/CollectionImage";

export interface CollectionCreatePageFormData extends MetadataFormData {
  backgroundImage: {
    url: string;
    value: string;
  };
  backgroundImageAlt: string;
  description: RawDraftContentState;
  name: string;
  url: string;
  slug: string;
  publicationDate: string;
  isPublished: boolean;
  seoDescription: string;
  seoTitle: string;
  seoKeyword: string;
  usedFor: CollectionUsedForEnum;
  displayOrder: number;
}

export interface CollectionCreatePageProps {
  disabled: boolean;
  errors: ProductErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onSubmit: (data: CollectionCreatePageFormData) => void;
}

const initialForm: CollectionCreatePageFormData = {
  backgroundImage: {
    url: null,
    value: null
  },
  backgroundImageAlt: "",
  description: convertToRaw(ContentState.createFromText("")),
  isPublished: false,
  metadata: [],
  name: "",
  url: "",
  privateMetadata: [],
  publicationDate: "",
  seoDescription: "",
  seoTitle: "",
  seoKeyword: "",
  slug: "",
  usedFor: CollectionUsedForEnum.GENERAL,
  displayOrder: 999
};

const CollectionCreatePage: React.FC<CollectionCreatePageProps> = ({
  disabled,
  errors,
  saveButtonBarState,
  onBack,
  onSubmit
}: CollectionCreatePageProps) => {
  const intl = useIntl();
  const UsedForChoices = [
    {
      label: intl.formatMessage({
        defaultMessage: "General",
        description: "collection used for choice"
      }),
      value: CollectionUsedForEnum.GENERAL
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Search Page",
        description: "collection used for choice"
      }),
      value: CollectionUsedForEnum.SEARCH_PAGE
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Product Page",
        description: "collection used for choice"
      }),
      value: CollectionUsedForEnum.PRODUCT_PAGE
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Empty Shopping Bag",
        description: "collection used for choice"
      }),
      value: CollectionUsedForEnum.EMPTY_SHOPPING_BAG
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Component Page",
        description: "collection used for choice"
      }),
      value: CollectionUsedForEnum.COMPONENT_PAGE
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Product Popup Message",
        description: "product popup message used for choice"
      }),
      value: CollectionUsedForEnum.PRODUCT_POPUP_MESSAGE
    }
  ];
  const localizeDate = useDateLocalize();
  const {
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  return (
    <Form initial={initialForm} onSubmit={onSubmit}>
      {({ change, data, hasChanged, submit }) => {
        const changeMetadata = makeMetadataChangeHandler(change);

        return (
          <Container>
            <AppHeader onBack={onBack}>
              {intl.formatMessage(sectionNames.recommendationgroups)}
            </AppHeader>
            <PageHeader
              title={intl.formatMessage({
                defaultMessage: "Add Recommendation Group",
                description: "page header"
              })}
            />
            <Grid>
              <div>
                <CollectionDetails
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                />
                <CardSpacer />
                <CollectionImage
                  image={
                    data.backgroundImage.url
                      ? {
                          __typename: "Image",
                          alt: data.backgroundImageAlt,
                          url: data.backgroundImage.url
                        }
                      : null
                  }
                  onImageDelete={() =>
                    change({
                      target: {
                        name: "backgroundImage",
                        value: {
                          url: null,
                          value: null
                        }
                      }
                    } as any)
                  }
                  onImageUpload={file =>
                    change({
                      target: {
                        name: "backgroundImage",
                        value: {
                          url: URL.createObjectURL(file),
                          value: file
                        }
                      }
                    } as any)
                  }
                  onChange={change}
                  data={data}
                />
                <CardSpacer />
                <SeoForm
                  allowEmptySlug={true}
                  description={data.seoDescription}
                  disabled={disabled}
                  descriptionPlaceholder=""
                  helperText={intl.formatMessage({
                    defaultMessage:
                      "Add search engine title and description to make this recommendation group easier to find"
                  })}
                  slug={data.slug}
                  slugPlaceholder={data.name}
                  title={data.seoTitle}
                  keyword={data.seoKeyword}
                  titlePlaceholder={data.name}
                  keywordPlaceholder={data.name}
                  onChange={change}
                />
                <CardSpacer />
                <Metadata data={data} onChange={changeMetadata} />
              </div>
              <div>
                <VisibilityCard
                  data={data}
                  errors={errors}
                  disabled={disabled}
                  messages={{
                    hiddenLabel: intl.formatMessage({
                      defaultMessage: "Hidden",
                      description: "recommendation group label"
                    }),
                    hiddenSecondLabel: intl.formatMessage(
                      {
                        defaultMessage: "will be visible from {date}",
                        description: "recommendation group"
                      },
                      {
                        date: localizeDate(data.publicationDate, "L")
                      }
                    ),
                    visibleLabel: intl.formatMessage({
                      defaultMessage: "Visible",
                      description: "recommendation group label"
                    })
                  }}
                  onChange={change}
                />
                <CardSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "About This Group",
                      description: "section header"
                    })}
                  />
                  <CardContent>
                    <SingleSelectField
                      disabled={disabled}
                      choices={UsedForChoices}
                      label={intl.formatMessage({
                        defaultMessage: "Used For",
                        description: "Collection used for choice."
                      })}
                      name="usedFor"
                      onChange={change}
                      value={data.usedFor}
                    />
                  </CardContent>
                </Card>
              </div>
            </Grid>
            <SaveButtonBar
              state={saveButtonBarState}
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onSave={submit}
            />
          </Container>
        );
      }}
    </Form>
  );
};
CollectionCreatePage.displayName = "CollectionCreatePage";
export default CollectionCreatePage;
